/**
 * Score accumulation, streaks and end-of-game bonuses.
 *
 * Streak model, straight from the original `ScoreManager`: a counter of
 * *consecutive scoring resolutions*. The first scoring play in a run pays no
 * streak bonus (level 0); each further one steps up through +250 / +500 / +750 /
 * +1000 / +1250, labelled 2x…6x. Any play that scores nothing — a plain landing
 * or a bust — resets the run to zero.
 *
 * The original silently paid nothing beyond the sixth consecutive score (its
 * `if (BounesCount == n)` ladder simply ran out). That is clearly a slip rather
 * than a design, so the level clamps at 5 (6x) and keeps paying +1250.
 */

import { levelFor } from '@/game/levels';
import {
  POINTS,
  STREAK_LABELS,
  type FinalScore,
  type GameOverReason,
} from '@/game/types';
import type { PileOutcome } from '@/game/rules';

/** Highest streak level; level 5 is the 6x callout. */
export const MAX_STREAK_LEVEL = POINTS.streak.length;

/** Everything the scorer needs to carry between plays. */
export interface ScoreState {
  score: number;
  busts: number;
  /** Consecutive scoring resolutions, 0 when the run is broken. */
  streak: number;
  /**
   * Piles cleared this round.
   *
   * 21 Wild's repeatable unit of progress — the thing a player does over and
   * over that says the round is going somewhere. Kept here rather than counted
   * from the event stream so that undo rewinds it with everything else.
   */
  clears: number;
  /** The highest streak level reached this round, 0..5. Never falls. */
  bestStreak: number;
  /**
   * Cards played this round.
   *
   * Only reason it exists: a round where nobody did anything earns nothing at
   * all. Without it, opening a challenge and letting the clock run out is a
   * source of progression, which is a reason to do exactly that.
   */
  plays: number;
}

export function initialScoreState(): ScoreState {
  return { score: 0, busts: 0, streak: 0, clears: 0, bestStreak: 0, plays: 0 };
}

/** Streak level (0 = no callout, 1..5 = 2x..6x) for a run of `consecutive`. */
export function streakLevelOf(consecutive: number): number {
  if (consecutive < 2) return 0;
  return Math.min(consecutive - 1, MAX_STREAK_LEVEL);
}

/** Flat bonus for a streak level. Level 0 pays nothing. */
export function streakBonusOf(level: number): number {
  if (level < 1) return 0;
  // Clamped to the table's length by MAX_STREAK_LEVEL, and level >= 1 here.
  return POINTS.streak[Math.min(level, MAX_STREAK_LEVEL) - 1]!;
}

/** `2x`…`6x`, or null when there is no streak callout to show. */
export function streakLabelOf(level: number): string | null {
  if (level < 1) return null;
  return STREAK_LABELS[Math.min(level, MAX_STREAK_LEVEL) - 1]!;
}

/**
 * A stored streak LEVEL (what `bestStreak` holds: 0 none, 1..5 the 2x..6x
 * callouts) as the multiplier a person reads: `5X`, or `\u2014` when there was none.
 * Every screen that shows a stored best streak goes through here; the level
 * itself is never the label (a level-4 best is "5X", not "4X").
 */
export function streakMultiplierLabel(level: number): string {
  const label = streakLabelOf(Number.isFinite(level) ? Math.floor(level) : 0);
  return label === null ? '\u2014' : label.toUpperCase();
}

/** The scoring consequences of a single resolution. */
export interface ScoredOutcome {
  state: ScoreState;
  basePoints: number;
  streakLevel: number;
  streakPoints: number;
  streakLabel: string | null;
  /** basePoints + streakPoints */
  pointsGained: number;
}

/**
 * Fold one pile outcome into the running score.
 * Pure: `state` is not mutated, the next state comes back in `state`.
 */
export function applyOutcome(state: ScoreState, outcome: PileOutcome): ScoredOutcome {
  const scored = outcome.basePoints > 0;
  const streak = scored ? state.streak + 1 : 0;
  const streakLevel = scored ? streakLevelOf(streak) : 0;
  const streakPoints = streakBonusOf(streakLevel);
  const pointsGained = outcome.basePoints + streakPoints;

  return {
    state: {
      score: state.score + pointsGained,
      busts: state.busts + (outcome.isBust ? 1 : 0),
      streak,
      clears: state.clears + (outcome.cleared ? 1 : 0),
      bestStreak: Math.max(state.bestStreak, streakLevel),
      plays: state.plays + 1,
    },
    basePoints: outcome.basePoints,
    streakLevel,
    streakPoints,
    streakLabel: streakLabelOf(streakLevel),
    pointsGained,
  };
}

/**
 * `ceil(secondsRemaining × (10 − 0.2 × cardsRemaining))`, floored at 0.
 *
 * Evaluated as `ceil(seconds × (50 − cards) / 5)` so that binary floating point
 * cannot nudge an exact result over an integer boundary and steal a point.
 */
export function timeBonus(secondsRemaining: number, cardsRemaining: number): number {
  const seconds = Math.max(0, secondsRemaining);
  const cards = Math.max(0, cardsRemaining);
  const inverseDecay = 1 / POINTS.timeBonusDecayPerCard;
  const scaledRate = POINTS.timeBonusRate * inverseDecay - cards;
  return Math.max(0, Math.ceil((seconds * scaledRate) / inverseDecay));
}

export interface FinalScoreInput {
  /** Score accumulated during play, before end-of-game bonuses. */
  score: number;
  busts: number;
  clears: number;
  bestStreak: number;
  plays: number;
  secondsRemaining: number;
  /** Cards never resolved: stock plus everything still sitting in the piles. */
  cardsRemaining: number;
  /** Stock empty *and* all four piles empty. */
  perfectGame: boolean;
  reason: GameOverReason;
}

/** End-of-game breakdown: perfect game, no-bust, time bonus, total. */
export function finalScore(input: FinalScoreInput): FinalScore {
  const perfectGameBonus = input.perfectGame ? POINTS.perfectGame : 0;
  const noBustBonus = input.busts === 0 ? POINTS.noBust : 0;
  const bonus = timeBonus(input.secondsRemaining, input.cardsRemaining);

  return {
    base: input.score,
    perfectGameBonus,
    noBustBonus,
    timeBonus: bonus,
    total: input.score + perfectGameBonus + noBustBonus + bonus,
    busts: input.busts,
    clears: input.clears,
    bestStreak: input.bestStreak,
    plays: input.plays,
    secondsRemaining: input.secondsRemaining,
    cardsRemaining: input.cardsRemaining,
    perfectGame: input.perfectGame,
    reason: input.reason,
    // From the in-play score only — deliberately computed before the three
    // bonuses above are added, so a time bonus can never hand out a level the
    // player did not play for.
    levelReached: levelFor(input.score),
  };
}
